import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, deleteField, doc, getDoc, getDocs, orderBy, query, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, listAll, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../shared/storage/firebase';
import { useAuth } from '../domains/auth/contexts/AuthContext';
import GoogleMapsService from '../shared/services/GoogleMapsService';
import '../shared/styles/globals/App.css';
import './TeacherProfile.css';
import type { TeacherBgCheckType, TeacherSchoolClearanceItem, UploadedFileMeta } from '../types';

type GooglePlacePrediction = {
  description: string;
  placeId: string;
};

type NotificationChannel = 'inApp' | 'email' | 'sms' | 'slack';
type AdminNotificationEvent = 'urgentCoverage' | 'scheduleChanges' | 'newApplications' | 'onboarding' | 'partnerUpdates' | 'billing' | 'operations' | 'reports';
type AdminNotificationPreferences = {
  paused: boolean;
  channels: Record<NotificationChannel, boolean>;
  events: Record<AdminNotificationEvent, Record<NotificationChannel, boolean>>;
  digest: 'instant' | 'daily' | 'weekly';
  digestTime: string;
  quietHoursEnabled: boolean;
  quietStart: string;
  quietEnd: string;
  urgentOverride: boolean;
};

const NOTIFICATION_CHANNELS: Array<{ id: NotificationChannel; label: string; icon: string }> = [
  { id: 'inApp', label: 'In app', icon: '●' }, { id: 'email', label: 'Email', icon: '✉' },
  { id: 'sms', label: 'Text', icon: '▤' }, { id: 'slack', label: 'Slack', icon: '#' },
];
const ADMIN_NOTIFICATION_GROUPS: Array<{ label: string; description: string; events: Array<{ id: AdminNotificationEvent; label: string; description: string; accent: string; icon: string }> }> = [
  { label: 'People & scheduling', description: 'Stay ahead of staffing changes and teacher activity.', events: [
    { id: 'urgentCoverage', label: 'Urgent coverage', description: 'Open classes, last-minute callouts, and unclaimed subs', accent: '#ef4444', icon: '!' },
    { id: 'scheduleChanges', label: 'Schedule changes', description: 'Class moves, cancellations, and assignment updates', accent: '#8b5cf6', icon: '↻' },
    { id: 'newApplications', label: 'Hiring emails', description: 'Application alerts and copies of applicant emails', accent: '#0ea5e9', icon: '+' },
    { id: 'onboarding', label: 'Teacher onboarding', description: 'Training, certification, and clearance milestones', accent: '#14b8a6', icon: '✓' },
  ]},
  { label: 'Business & operations', description: 'Choose how the organization keeps you informed.', events: [
    { id: 'partnerUpdates', label: 'Partner activity', description: 'New partners, contacts, and school updates', accent: '#f59e0b', icon: '⌂' },
    { id: 'billing', label: 'Billing email copies', description: 'BCC copies of invoices plus payment activity', accent: '#22c55e', icon: '$' },
    { id: 'operations', label: 'Operations email copies', description: 'CC copies of school logistics and operational alerts', accent: '#f97316', icon: '◆' },
    { id: 'reports', label: 'Reports & insights', description: 'Weekly performance and growth summaries', accent: '#6366f1', icon: '↗' },
  ]},
];
const DEFAULT_ADMIN_NOTIFICATION_PREFERENCES: AdminNotificationPreferences = {
  paused: false, channels: { inApp: true, email: true, sms: false, slack: true },
  events: {
    urgentCoverage: { inApp: true, email: true, sms: true, slack: true }, scheduleChanges: { inApp: true, email: true, sms: false, slack: true },
    newApplications: { inApp: true, email: true, sms: false, slack: false }, onboarding: { inApp: true, email: true, sms: false, slack: false },
    partnerUpdates: { inApp: true, email: true, sms: false, slack: false }, billing: { inApp: true, email: true, sms: false, slack: false },
    operations: { inApp: true, email: true, sms: false, slack: true }, reports: { inApp: false, email: true, sms: false, slack: false },
  },
  digest: 'instant', digestTime: '09:00', quietHoursEnabled: true, quietStart: '20:00', quietEnd: '08:00', urgentOverride: true,
};
function normalizeAdminNotificationPreferences(value: any): AdminNotificationPreferences {
  const defaults = DEFAULT_ADMIN_NOTIFICATION_PREFERENCES;
  return { ...defaults, ...(value || {}), channels: { ...defaults.channels, ...(value?.channels || {}) },
    events: Object.fromEntries(Object.entries(defaults.events).map(([event, settings]) => [event, { ...settings, ...(value?.events?.[event] || {}) }])) as AdminNotificationPreferences['events'] };
}

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

const SUBJECT_OPTIONS: Array<{ value: 'music' | 'ai'; label: string }> = [
  { value: 'music', label: 'Music' },
  { value: 'ai', label: 'AI' },
];

const US_STATE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const BIO_MAX_WORDS = 100;

const MUSIC_TOPIC_OPTIONS: Array<{ value: string; label: string }> = [
  // Core concepts
  { value: 'music_theory', label: 'Music theory' },
  { value: 'ear_training', label: 'Aural skills' },
  { value: 'sight_reading', label: 'Sight reading' },
  { value: 'rhythm', label: 'Rhythm' },
  { value: 'composition', label: 'Composition' },
  { value: 'improvisation', label: 'Improvisation' },
  { value: 'songwriting', label: 'Songwriting' },
  { value: 'arranging', label: 'Arranging' },
  { value: 'music_production', label: 'Music production' },

  // Instruments
  { value: 'voice', label: 'Voice' },
  { value: 'piano', label: 'Piano' },
  { value: 'guitar', label: 'Guitar' },
  { value: 'bass_guitar', label: 'Bass guitar' },
  { value: 'ukulele', label: 'Ukulele' },
  { value: 'drums', label: 'Drums' },
  { value: 'percussion', label: 'Percussion' },

  // Strings
  { value: 'violin', label: 'Violin' },
  { value: 'viola', label: 'Viola' },
  { value: 'cello', label: 'Cello' },
  { value: 'double_bass', label: 'Double bass' },

  // Woodwinds
  { value: 'flute', label: 'Flute' },
  { value: 'clarinet', label: 'Clarinet' },
  { value: 'saxophone', label: 'Saxophone' },
  { value: 'oboe', label: 'Oboe' },
  { value: 'bassoon', label: 'Bassoon' },

  // Brass
  { value: 'trumpet', label: 'Trumpet' },
  { value: 'trombone', label: 'Trombone' },
  { value: 'french_horn', label: 'French horn' },
  { value: 'tuba', label: 'Tuba' },
];

function digitsOnly(input: string): string {
  return String(input || '').replace(/\D/g, '');
}

function countWords(input: string): number {
  return String(input || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function clampToMaxWords(input: string, maxWords: number): string {
  const trimmed = String(input || '').trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return input; // preserve original spacing as-typed when under limit
  return words.slice(0, maxWords).join(' ');
}

function formatUsPhoneDisplay(input: string): string {
  const raw = digitsOnly(input);
  if (!raw) return '';

  // If user includes a leading US country code, keep it visible.
  const hasUsCountryCode = raw.length >= 11 && raw.startsWith('1');
  const digits = hasUsCountryCode ? raw.slice(1) : raw;
  const prefix = hasUsCountryCode ? '1 ' : '';

  if (digits.length <= 3) return `${prefix}(${digits}`;
  if (digits.length <= 6) return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  const main = `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  const rest = digits.length > 10 ? ` ${digits.slice(10)}` : '';
  return `${main}${rest}`;
}

function splitName(fullName: string): { firstName: string; lastName: string } {
  const normalized = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return { firstName: '', lastName: '' };
  const parts = normalized.split(' ');
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

const AdminNotificationSettings: React.FC<{
  preferences: AdminNotificationPreferences; onChange: (value: AdminNotificationPreferences) => void;
  email: string; phone: string; slackEmail: string; saving: boolean; onSave: () => void;
}> = ({ preferences, onChange, email, phone, slackEmail, saving, onSave }) => {
  const toggleChannel = (channel: NotificationChannel) => {
    const enabled = !preferences.channels[channel];
    const events = Object.fromEntries(Object.entries(preferences.events).map(([id, values]) => [id, { ...values, [channel]: enabled }])) as AdminNotificationPreferences['events'];
    onChange({ ...preferences, channels: { ...preferences.channels, [channel]: enabled }, events });
  };
  const enabledCount = Object.values(preferences.events).reduce((total, channels) => total + Object.values(channels).filter(Boolean).length, 0);
  return <div className="admin-notifications">
    <div className={`admin-notifications-status${preferences.paused ? ' is-paused' : ''}`}>
      <div className="admin-notifications-status-icon">{preferences.paused ? '—' : '✓'}</div>
      <div className="admin-notifications-status-copy"><strong>{preferences.paused ? 'Notifications are paused' : 'Notifications are active'}</strong><span>{preferences.paused ? 'Only mandatory account and security messages will be sent.' : `${enabledCount} event and channel combinations are enabled.`}</span></div>
      <label className="admin-notifications-switch-row"><span>{preferences.paused ? 'Resume all' : 'Pause all'}</span><span className="admin-notifications-switch"><input type="checkbox" checked={!preferences.paused} onChange={() => onChange({ ...preferences, paused: !preferences.paused })} aria-label="Notifications active" /><span /></span></label>
    </div>
    <section className="admin-notifications-card admin-notifications-delivery">
      <div className="admin-notifications-card-heading"><div><h2>Delivery methods</h2><p>Turn a channel on or off everywhere with one click.</p></div></div>
      <div className="admin-notifications-channel-grid">{NOTIFICATION_CHANNELS.map(channel => <button key={channel.id} type="button" className={preferences.channels[channel.id] ? 'is-active' : ''} onClick={() => toggleChannel(channel.id)} aria-pressed={preferences.channels[channel.id]}><span className="admin-notifications-channel-icon">{channel.icon}</span><span><strong>{channel.label}</strong><small>{preferences.channels[channel.id] ? 'Enabled' : 'Off'}</small></span><span className="admin-notifications-channel-check">{preferences.channels[channel.id] ? '✓' : ''}</span></button>)}</div>
      <div className="admin-notifications-destinations"><span><strong>Email</strong>{email || 'No email connected'}</span><span><strong>Text</strong>{phone || 'Add a phone number in Profile'}</span><span><strong>Slack</strong>{slackEmail || 'Add your Slack email in Profile'}</span></div>
    </section>
    {ADMIN_NOTIFICATION_GROUPS.map(group => <section className="admin-notifications-card admin-notifications-matrix" key={group.label}>
      <div className="admin-notifications-card-heading"><div><h2>{group.label}</h2><p>{group.description} Email includes messages sent directly to you and emails where you are CC’d or BCC’d.</p></div></div>
      <div className="admin-notifications-matrix-header" aria-hidden="true"><span />{NOTIFICATION_CHANNELS.map(channel => <span key={channel.id}>{channel.label}</span>)}</div>
      {group.events.map(event => <div className="admin-notifications-event" key={event.id}><div className="admin-notifications-event-label"><span className="admin-notifications-event-icon" style={{ background: `${event.accent}18`, color: event.accent }}>{event.icon}</span><span><strong>{event.label}</strong><small>{event.description}</small></span></div>{NOTIFICATION_CHANNELS.map(channel => { const checked = preferences.events[event.id][channel.id]; return <label key={channel.id} className="admin-notifications-cell" title={`${event.label} via ${channel.label}`}><span className="admin-notifications-mobile-channel">{channel.label}</span><input type="checkbox" checked={checked} disabled={!preferences.channels[channel.id]} onChange={() => onChange({ ...preferences, events: { ...preferences.events, [event.id]: { ...preferences.events[event.id], [channel.id]: !checked } } })} /><span>✓</span></label>; })}</div>)}
    </section>)}
    <section className="admin-notifications-card admin-notifications-schedule">
      <div className="admin-notifications-card-heading"><div><h2>Timing & focus</h2><p>Bundle routine updates and protect your off-hours.</p></div></div>
      <div className="admin-notifications-form-grid"><label><span>Routine updates</span><select value={preferences.digest} onChange={e => onChange({ ...preferences, digest: e.target.value as AdminNotificationPreferences['digest'] })}><option value="instant">Send instantly</option><option value="daily">Daily digest</option><option value="weekly">Weekly digest</option></select><small>Urgent coverage is always sent instantly.</small></label>{preferences.digest !== 'instant' && <label><span>Digest time</span><input type="time" value={preferences.digestTime} onChange={e => onChange({ ...preferences, digestTime: e.target.value })} /><small>Uses your profile time zone.</small></label>}</div>
      <div className="admin-notifications-rule"><label className="admin-notifications-switch-row"><span><strong>Quiet hours</strong><small>Silence notifications during a daily window.</small></span><span className="admin-notifications-switch"><input type="checkbox" checked={preferences.quietHoursEnabled} onChange={() => onChange({ ...preferences, quietHoursEnabled: !preferences.quietHoursEnabled })} /><span /></span></label>{preferences.quietHoursEnabled && <div className="admin-notifications-time-range"><label>From <input type="time" value={preferences.quietStart} onChange={e => onChange({ ...preferences, quietStart: e.target.value })} /></label><span>to</span><label><input aria-label="Quiet hours end" type="time" value={preferences.quietEnd} onChange={e => onChange({ ...preferences, quietEnd: e.target.value })} /></label></div>}</div>
      <div className="admin-notifications-rule"><label className="admin-notifications-switch-row"><span><strong>Let urgent alerts through</strong><small>Urgent coverage can bypass quiet hours.</small></span><span className="admin-notifications-switch"><input type="checkbox" checked={preferences.urgentOverride} onChange={() => onChange({ ...preferences, urgentOverride: !preferences.urgentOverride })} /><span /></span></label></div>
    </section>
    <div className="admin-notifications-footer"><span>Preferences apply only to your admin account.</span><button type="button" className="teacher-profile-save" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save notification settings'}</button></div>
  </div>;
};

const TeacherProfilePage: React.FC = () => {
  const { userData } = useAuth();
  const isAdmin = userData?.isAdmin === true;
  const [profileView, setProfileView] = useState<'profile' | 'notifications'>('profile');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const lastUserIdRef = useRef<string | null>(null);

  // Trainings / certifications (stored in /teachers/{teacherId})
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [compliance, setCompliance] = useState<{
    mandatoryReportingCertificationFile?: UploadedFileMeta | null;
    signedContracts?: UploadedFileMeta[];
  } | null>(null);

  const [schoolClearancesLoading, setSchoolClearancesLoading] = useState(false);
  const [schoolClearancesError, setSchoolClearancesError] = useState<string | null>(null);
  const [schoolClearances, setSchoolClearances] = useState<
    Array<{ schoolId: string; schoolName?: string | null; items: TeacherSchoolClearanceItem[]; historical?: boolean }>
  >([]);

  const [schoolsForClearances, setSchoolsForClearances] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedClearanceSchoolId, setSelectedClearanceSchoolId] = useState<string>('');
  const [selectedClearanceType, setSelectedClearanceType] = useState<TeacherBgCheckType>('cori');
  const [selectedClearanceOtherLabel, setSelectedClearanceOtherLabel] = useState<string>('');
  const [uploadingComplianceKey, setUploadingComplianceKey] = useState<string | null>(null);
  
  // Headshot upload state
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [headshotError, setHeadshotError] = useState<string | null>(null);
  const [headshotSuccess, setHeadshotSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localHeadshotPreviewUrl, setLocalHeadshotPreviewUrl] = useState<string | null>(null);

  const [uploadedPhotos, setUploadedPhotos] = useState<Array<{ path: string; name: string; url: string }>>([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsError, setUploadsError] = useState<string | null>(null);

  const previousPreviewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = previousPreviewUrlRef.current;
    if (prev && prev !== localHeadshotPreviewUrl) {
      try { URL.revokeObjectURL(prev); } catch {}
    }
    previousPreviewUrlRef.current = localHeadshotPreviewUrl;
  }, [localHeadshotPreviewUrl]);

  // Field-level completeness (based on SAVED userData, not local edits)
  const missing = useMemo(() => {
    const name = splitName(String(userData?.displayName || ''));
    const phoneDigits = digitsOnly(String(userData?.phoneNumber || ''));
    const phoneOk = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));

    const ship = (userData as any)?.shippingAddress || null;
    const shippingLine1 = String(ship?.line1 || '').trim();
    const shippingCity = String(ship?.city || '').trim();
    const shippingState = String(ship?.state || '').trim();
    const shippingZip = String(ship?.zip || '').trim();
    const shippingCountry = String(ship?.country || '').trim();

    const slackEmail = String((userData as any)?.slackEmail || '').trim();
    const tz = String((userData as any)?.timeZone || '').trim();
    const subjects = Array.isArray((userData as any)?.subjects) ? (userData as any).subjects : [];
    const subjectsNormalized = subjects.map((s: any) => String(s).trim().toLowerCase()).filter(Boolean);
    const hasMusic = subjectsNormalized.includes('music');

    const bio = String((userData as any)?.bio || '').trim();
    const bioOk = countWords(bio) > 0 && countWords(bio) <= BIO_MAX_WORDS;

    const musicPrivateLessons = (userData as any)?.musicPrivateLessons;
    const musicTopics = Array.isArray((userData as any)?.musicTopics) ? (userData as any).musicTopics : [];
    const musicPrivateLessonsSet = typeof musicPrivateLessons === 'boolean';
    const musicTopicsOk = !hasMusic || musicPrivateLessons !== true || (Array.isArray(musicTopics) && musicTopics.length > 0);

    const emergencyName = splitName(String((userData as any)?.emergencyContact?.name || ''));
    const emergencyPhoneDigits = digitsOnly(String((userData as any)?.emergencyContact?.phone || ''));
    const emergencyPhoneOk =
      emergencyPhoneDigits.length === 10 || (emergencyPhoneDigits.length === 11 && emergencyPhoneDigits.startsWith('1'));
    const emergencyRel = String((userData as any)?.emergencyContact?.relationship || '').trim();

    const hasCarSet = typeof (userData as any)?.hasCar === 'boolean';
    const hasLicenseSet = typeof (userData as any)?.hasDriversLicense === 'boolean';

    return {
      firstName: !name.firstName.trim(),
      lastName: !name.lastName.trim(),
      bio: !bioOk,
      slackEmail: !slackEmail,
      phoneNumber: !phoneOk,
      shippingLine1: !shippingLine1,
      shippingCity: !shippingCity,
      shippingState: !shippingState,
      shippingZip: !shippingZip,
      shippingCountry: !shippingCountry,
      timeZone: !tz,
      subjects: !(Array.isArray(subjects) && subjects.length > 0),
      musicPrivateLessons: hasMusic && !musicPrivateLessonsSet,
      musicTopics: hasMusic && musicPrivateLessons === true && !musicTopicsOk,
      hasCar: !hasCarSet,
      hasDriversLicense: !hasLicenseSet,
      emergencyFirstName: !emergencyName.firstName.trim(),
      emergencyLastName: !emergencyName.lastName.trim(),
      emergencyPhone: !emergencyPhoneOk,
      emergencyRelationship: !emergencyRel,
    };
  }, [userData]);

  const [shippingAddressGooglePredictions, setShippingAddressGooglePredictions] = useState<GooglePlacePrediction[]>([]);
  const [shippingAddressGoogleLoading, setShippingAddressGoogleLoading] = useState(false);
  const [shippingAddressGoogleError, setShippingAddressGoogleError] = useState<string | null>(null);
  const googleMapsLoadPromiseRef = useRef<Promise<void> | null>(null);
  const shippingAddressFieldRef = useRef<HTMLDivElement | null>(null);
  const [isShippingAddressInputFocused, setIsShippingAddressInputFocused] = useState(false);

  const closeShippingAddressSuggestions = useCallback(() => {
    setShippingAddressGooglePredictions([]);
    setShippingAddressGoogleLoading(false);
    setShippingAddressGoogleError(null);
    setIsShippingAddressInputFocused(false);
  }, []);

  const normalizeGoogleAddressLabel = useCallback((description: string) => {
    const raw = String(description || '').trim();
    return raw
      .replace(/,\s*USA\s*$/i, '')
      .replace(/,\s*United States\s*$/i, '')
      .replace(/,\s*United States of America\s*$/i, '')
      .trim();
  }, []);

  const ensureGooglePlacesReady = useCallback(async () => {
    if (GoogleMapsService.isApiLoaded()) return;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      throw new Error('Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_API_KEY).');
    }
    if (!googleMapsLoadPromiseRef.current) {
      googleMapsLoadPromiseRef.current = GoogleMapsService.load(apiKey);
    }
    await googleMapsLoadPromiseRef.current;
  }, []);

  const applyShippingAddressFromPlaceId = useCallback(async (placeId: string) => {
    const pid = String(placeId || '').trim();
    if (!pid) return;
    await ensureGooglePlacesReady();
    const g = (window as any).google;

    const details = await new Promise<any>((resolve, reject) => {
      try {
        const svc = new g.maps.places.PlacesService(document.createElement('div'));
        svc.getDetails(
          {
            placeId: pid,
            fields: ['address_components', 'formatted_address'],
          },
          (place: any, status: any) => {
            const ok = status === g.maps.places.PlacesServiceStatus.OK;
            if (!ok || !place) {
              reject(new Error('Failed to fetch address details from Google Places.'));
              return;
            }
            resolve(place);
          }
        );
      } catch (e) {
        reject(e);
      }
    });

    const comps: any[] = Array.isArray(details?.address_components) ? details.address_components : [];
    const formatted = String(details?.formatted_address || '').trim();

    const byType = (type: string) => comps.find(c => Array.isArray(c?.types) && c.types.includes(type));
    const streetNumber = String(byType('street_number')?.short_name || '').trim();
    const route = String(byType('route')?.short_name || '').trim();
    const subpremise = String(byType('subpremise')?.short_name || '').trim(); // optional, we won't auto-fill line2
    const city =
      String(byType('locality')?.long_name || '').trim() ||
      String(byType('postal_town')?.long_name || '').trim() ||
      String(byType('sublocality')?.long_name || '').trim() ||
      String(byType('sublocality_level_1')?.long_name || '').trim();
    const state = String(byType('administrative_area_level_1')?.short_name || '').trim();
    const zip = String(byType('postal_code')?.short_name || '').trim();
    const zipSuffix = String(byType('postal_code_suffix')?.short_name || '').trim();
    const country = String(byType('country')?.long_name || '').trim();

    const zipFull = zip && zipSuffix ? `${zip}-${zipSuffix}` : zip;
    const line1FromComponents = streetNumber && route ? `${streetNumber} ${route}` : '';
    const line1FromFormatted = formatted ? formatted.split(',')[0].trim() : '';
    const line1 = line1FromComponents || line1FromFormatted;

    setForm(v => ({
      ...(v as any),
      shippingLine1: line1 || (v as any).shippingLine1 || '',
      // Don't auto-fill line2 (apt/unit); if Google provides subpremise, let it be a hint only.
      shippingLine2: (v as any).shippingLine2 || (subpremise ? `Apt ${subpremise}` : ''),
      shippingCity: city || (v as any).shippingCity || '',
      shippingState: state || (v as any).shippingState || '',
      shippingZip: zipFull || (v as any).shippingZip || '',
      shippingCountry: country || (v as any).shippingCountry || 'United States',
      shippingPlaceId: pid,
      shippingFormattedAddress: formatted || (v as any).shippingFormattedAddress || '',
    }));
  }, [ensureGooglePlacesReady]);

  const initial = useMemo(() => ({
    shippingLine1: String(((userData as any)?.shippingAddress?.line1 || '')).toString(),
    shippingLine2: String(((userData as any)?.shippingAddress?.line2 || '')).toString(),
    shippingCity: String(((userData as any)?.shippingAddress?.city || '')).toString(),
    shippingState: String(((userData as any)?.shippingAddress?.state || '')).toString(),
    shippingZip: String(((userData as any)?.shippingAddress?.zip || '')).toString(),
    shippingCountry: String(((userData as any)?.shippingAddress?.country || 'United States')).toString(),
    shippingPlaceId: String(((userData as any)?.shippingAddress?.placeId || '')).toString(),
    shippingFormattedAddress: String(((userData as any)?.shippingAddress?.formattedAddress || '')).toString(),
    firstName: splitName((userData?.displayName || '').toString()).firstName,
    lastName: splitName((userData?.displayName || '').toString()).lastName,
    bio: ((userData as any)?.bio || '').toString(),
    phoneNumber: digitsOnly((userData?.phoneNumber || '').toString()),
    classReminderEmail: (userData as any)?.classReminderPreferences?.email !== false,
    classReminderSms:
      (userData as any)?.classReminderPreferences?.sms === true &&
      !!(userData as any)?.classReminderPreferences?.smsConsentedAt,
    slackEmail: ((userData as any)?.slackEmail || '').toString(),
    linkedinUrl: ((userData as any)?.linkedinUrl || '').toString(),
    hasCar: (typeof (userData as any)?.hasCar === 'boolean') ? !!(userData as any).hasCar : null as boolean | null,
    hasDriversLicense: (typeof (userData as any)?.hasDriversLicense === 'boolean') ? !!(userData as any).hasDriversLicense : null as boolean | null,
    timeZone: (userData?.timeZone || 'America/New_York').toString(),
    subjects: Array.isArray(userData?.subjects) ? (userData?.subjects as any[]).map(String) : ([] as string[]),
    musicPrivateLessons: (typeof (userData as any)?.musicPrivateLessons === 'boolean') ? !!(userData as any).musicPrivateLessons : null as boolean | null,
    musicTopics: Array.isArray((userData as any)?.musicTopics)
      ? (userData as any).musicTopics.map(String).filter(Boolean)
      : ([] as string[]),
    emergencyFirstName: splitName((userData?.emergencyContact?.name || '').toString()).firstName,
    emergencyLastName: splitName((userData?.emergencyContact?.name || '').toString()).lastName,
    emergencyPhone: digitsOnly((userData?.emergencyContact?.phone || '').toString()),
    emergencyRelationship: (userData?.emergencyContact?.relationship || '').toString(),
    adminNotificationPreferences: normalizeAdminNotificationPreferences((userData as any)?.adminNotificationPreferences),
  }), [
    userData?.displayName,
    (userData as any)?.bio,
    (userData as any)?.slackEmail,
    (userData as any)?.linkedinUrl,
    userData?.phoneNumber,
    (userData as any)?.classReminderPreferences?.email,
    (userData as any)?.classReminderPreferences?.sms,
    (userData as any)?.shippingAddress?.line1,
    (userData as any)?.shippingAddress?.line2,
    (userData as any)?.shippingAddress?.city,
    (userData as any)?.shippingAddress?.state,
    (userData as any)?.shippingAddress?.zip,
    (userData as any)?.shippingAddress?.country,
    (userData as any)?.shippingAddress?.placeId,
    (userData as any)?.shippingAddress?.formattedAddress,
    userData?.hasCar,
    userData?.hasDriversLicense,
    userData?.timeZone,
    userData?.subjects,
    (userData as any)?.musicPrivateLessons,
    (userData as any)?.musicTopics,
    userData?.emergencyContact?.name,
    userData?.emergencyContact?.phone,
    userData?.emergencyContact?.relationship,
    (userData as any)?.adminNotificationPreferences,
  ]);

  const [form, setForm] = useState(initial);

  // Hydrate form when userData arrives (avoid clobbering in-progress edits)
  useEffect(() => {
    const uid = userData?.id ?? null;
    if (!uid) return;

    // If switching accounts, always reset.
    if (lastUserIdRef.current !== uid) {
      lastUserIdRef.current = uid;
      setForm(initial);
      return;
    }

    // If we mounted before userData loaded and form is still basically empty, hydrate once.
    const isBlankProfile =
      !String(form.firstName || '').trim()
      && !String(form.lastName || '').trim()
      && !String(form.phoneNumber || '').trim()
      && !String((form as any).shippingLine1 || '').trim();

    if (isBlankProfile) {
      setForm(initial);
    }
  }, [userData?.id, initial]); // intentionally not depending on `form` to avoid loops

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const query = String((form as any).shippingLine1 || '').trim();
      if (query.length < 2) {
        setShippingAddressGooglePredictions([]);
        setShippingAddressGoogleLoading(false);
        setShippingAddressGoogleError(null);
        return;
      }

      setShippingAddressGoogleLoading(true);
      setShippingAddressGoogleError(null);

      try {
        await ensureGooglePlacesReady();
        const g = (window as any).google;

        // Prefer Places API (New): google.maps.places.AutocompleteSuggestion
        const fetchAutocompleteSuggestions =
          g?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;

        if (typeof fetchAutocompleteSuggestions === 'function') {
          const resp = await fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: ['us'],
          });
          if (cancelled) return;

          const suggestions = Array.isArray(resp?.suggestions) ? resp.suggestions : [];
          const next: GooglePlacePrediction[] = suggestions
            .slice(0, 8)
            .map((s: any) => {
              const pp = s?.placePrediction ?? s?.prediction ?? s;
              const placeId = String(pp?.placeId || pp?.place_id || '');
              const text =
                pp?.text?.text ||
                pp?.text ||
                pp?.description ||
                pp?.structuredFormat?.mainText?.text ||
                pp?.structured_formatting?.main_text ||
                '';
              const secondary =
                pp?.structuredFormat?.secondaryText?.text ||
                pp?.structured_formatting?.secondary_text ||
                '';
              const description = String(
                text && secondary ? `${text}, ${secondary}` : text || secondary || ''
              );
              return { description, placeId };
            })
            .filter((p: GooglePlacePrediction) => p.placeId.length > 0 && p.description.length > 0);

          setShippingAddressGooglePredictions(next);
          setShippingAddressGoogleLoading(false);
          return;
        }

        // Fallback to legacy AutocompleteService
        const LegacySvc = g?.maps?.places?.AutocompleteService;
        if (typeof LegacySvc === 'function') {
          const svc = new LegacySvc();
          svc.getPlacePredictions(
            {
              input: query,
              components: { country: 'us' },
              types: ['address'],
            },
            (preds: any[], status: string) => {
              if (cancelled) return;
              const ok = status === g.maps.places.PlacesServiceStatus.OK;
              if (!ok || !Array.isArray(preds)) {
                setShippingAddressGooglePredictions([]);
                setShippingAddressGoogleLoading(false);
                return;
              }
              const next: GooglePlacePrediction[] = preds
                .slice(0, 8)
                .map((p: any) => ({
                  description: String(p.description || ''),
                  placeId: String(p.place_id || ''),
                }))
                .filter((p: GooglePlacePrediction) => p.placeId.length > 0 && p.description.length > 0);
              setShippingAddressGooglePredictions(next);
              setShippingAddressGoogleLoading(false);
            }
          );
          return;
        }

        setShippingAddressGooglePredictions([]);
        setShippingAddressGoogleLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setShippingAddressGooglePredictions([]);
        setShippingAddressGoogleLoading(false);
        setShippingAddressGoogleError(e?.message ? String(e.message) : 'Failed to load Google Places.');
      }
    };

    const t = window.setTimeout(run, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [ensureGooglePlacesReady, (form as any).shippingLine1]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!isShippingAddressInputFocused) return;
      const root = shippingAddressFieldRef.current;
      if (!root) return;
      const target = event.target as Node | null;
      if (target && root.contains(target)) return;
      closeShippingAddressSuggestions();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [closeShippingAddressSuggestions, isShippingAddressInputFocused]);

  const loadUploadedPhotos = useCallback(async () => {
    if (!userData?.id) return;
    setUploadsError(null);
    setUploadsLoading(true);
    try {
      const folderRef = ref(storage, `teachers/${userData.id}/uploads`);
      const listing = await listAll(folderRef);
      const items = listing.items || [];

      const photos = await Promise.all(
        items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return { path: itemRef.fullPath, name: itemRef.name, url };
        })
      );

      // Sort newest-ish first (timestamp prefix if we use it, otherwise name)
      photos.sort((a, b) => b.name.localeCompare(a.name));
      setUploadedPhotos(photos);
    } catch (e: any) {
      console.warn('Failed to load uploaded photos:', e);
      setUploadsError(e?.message ? String(e.message) : 'Failed to load uploads.');
      setUploadedPhotos([]);
    } finally {
      setUploadsLoading(false);
    }
  }, [userData?.id]);

  useEffect(() => {
    if (!userData?.id) return;
    loadUploadedPhotos();
  }, [userData?.id, loadUploadedPhotos]);

  const loadCompliance = useCallback(async () => {
    if (!userData?.id) return;
    setComplianceLoading(true);
    setComplianceError(null);
    try {
      const ref = doc(db, 'teachers', userData.id);
      const snap = await getDoc(ref);
      const data = snap.exists() ? (snap.data() as any) : {};
      setCompliance({
        mandatoryReportingCertificationFile: (data?.mandatoryReportingCertificationFile as UploadedFileMeta) || null,
        signedContracts: Array.isArray(data?.signedContracts) ? (data.signedContracts as UploadedFileMeta[]) : [],
      });
    } catch (e: any) {
      console.warn('Failed to load teacher compliance doc:', e);
      setComplianceError(e?.message ? String(e.message) : 'Failed to load learning and certifications.');
      setCompliance(null);
    } finally {
      setComplianceLoading(false);
    }
  }, [userData?.id]);

  const loadSchoolClearances = useCallback(async () => {
    if (!userData?.id) return;
    setSchoolClearancesLoading(true);
    setSchoolClearancesError(null);
    try {
      const locationsSnap = await getDocs(collection(db, 'locations'));
      const partnerIds = Array.from(new Set(locationsSnap.docs
        .map((locationDoc) => String((locationDoc.data() as any)?.partnerId || '').trim())
        .filter(Boolean)));
      const canonicalSnaps = await Promise.all(partnerIds.map(partnerId =>
        getDoc(doc(db, 'partners', partnerId, 'teacherClearances', userData.id))
      ));
      const canonicalRows = canonicalSnaps.filter(snapshot => snapshot.exists()).map((snapshot) => {
        const value: any = snapshot.data() || {};
        const statuses = value.requirementStatuses && typeof value.requirementStatuses === 'object'
          ? Object.entries(value.requirementStatuses).map(([requirementId, raw]: [string, any]) => ({
              id: `${snapshot.ref.parent.parent?.id || ''}:${requirementId}`,
              type: requirementId as TeacherBgCheckType,
              typeLabel: `${requirementId.replace(/_/g, ' ')} — ${String(raw?.status || 'not started')}`,
              uploadedAt: raw?.completedAt || raw?.verifiedAt || null,
            } as TeacherSchoolClearanceItem))
          : [];
        return {
          schoolId: String(snapshot.ref.parent.parent?.id || value.partnerId || ''),
          schoolName: null,
          items: statuses,
          historical: false,
        };
      });
      const snap = await getDocs(collection(db, 'teachers', userData.id, 'schoolClearances'));
      const historicalRows = snap.docs.map((d) => {
        const v: any = d.data() || {};
        const items = Array.isArray(v.items) ? (v.items as TeacherSchoolClearanceItem[]) : [];
        return {
          schoolId: String(v.schoolId || d.id),
          schoolName: `${v.schoolName ? String(v.schoolName) : d.id} (historical)`,
          items,
          historical: true,
        };
      });
      const rows = [...canonicalRows, ...historicalRows];
      rows.sort((a, b) => String(a.schoolName || a.schoolId).localeCompare(String(b.schoolName || b.schoolId)));
      setSchoolClearances(rows);
    } catch (e: any) {
      console.warn('Failed to load school clearances:', e);
      setSchoolClearancesError(e?.message ? String(e.message) : 'Failed to load school clearances.');
      setSchoolClearances([]);
    } finally {
      setSchoolClearancesLoading(false);
    }
  }, [userData?.id]);

  const loadSchoolsForClearances = useCallback(async () => {
    try {
      // Teachers cannot read partner root documents, but they can read the
      // canonical location directory. Group locations by partner so the
      // clearance upload never depends on admin-only partner data.
      const q = query(collection(db, 'locations'), orderBy('name'));
      const snap = await getDocs(q);
      const locationsByPartner = new Map<string, string[]>();
      snap.docs.forEach((locationDoc) => {
        const value: any = locationDoc.data() || {};
        const partnerId = String(value.partnerId || '').trim();
        if (!partnerId) return;
        const locationName = String(value.name || '').trim();
        const names = locationsByPartner.get(partnerId) || [];
        if (locationName && !names.includes(locationName)) names.push(locationName);
        locationsByPartner.set(partnerId, names);
      });
      const rows = Array.from(locationsByPartner, ([id, locationNames]) => ({
        id,
        name: locationNames.length ? locationNames.join(' / ') : id,
      })).sort((a, b) => a.name.localeCompare(b.name));
      setSchoolsForClearances(rows);
    } catch (e) {
      console.warn('Failed to load partner locations for clearances', e);
      setSchoolsForClearances([]);
    }
  }, []);

  useEffect(() => {
    if (!userData?.id) return;
    loadCompliance();
    loadSchoolClearances();
    loadSchoolsForClearances();
  }, [userData?.id, loadCompliance, loadSchoolClearances, loadSchoolsForClearances]);

  const uploadTeacherComplianceFile = useCallback(async (
    kind: 'mandatoryReportingCertificationFile' | 'signedContract',
    file: File
  ) => {
    if (!userData?.id) return;
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setComplianceError('Please upload a PDF or an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setComplianceError('File too large. Maximum size is 10MB.');
      return;
    }

    setComplianceError(null);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const rand = Math.random().toString(16).slice(2, 8);
    const ext = (file.name.split('.').pop() || (isPdf ? 'pdf' : 'png')).toLowerCase().replace(/[^a-z0-9]/g, '');

    const folder =
      kind === 'mandatoryReportingCertificationFile'
        ? 'teachers/' + userData.id + '/compliance/mandatory-reporting'
        : 'teachers/' + userData.id + '/contracts';

    const storagePath = `${folder}/${stamp}-${rand}.${ext || (isPdf ? 'pdf' : 'png')}`;

    const key = `${kind}:${storagePath}`;
    setUploadingComplianceKey(key);
    try {
      const r = ref(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const url = await getDownloadURL(r);

      const meta: UploadedFileMeta = {
        storagePath,
        url,
        filename: file.name || undefined,
        contentType: file.type || undefined,
        sizeBytes: file.size || undefined,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData.id,
      };

      const teacherDocRef = doc(db, 'teachers', userData.id);
      if (kind === 'signedContract') {
        const prev: UploadedFileMeta[] =
          Array.isArray(compliance?.signedContracts) ? (compliance!.signedContracts as UploadedFileMeta[]) : [];
        const next = [meta, ...prev];
        await setDoc(teacherDocRef, { signedContracts: next, updatedAt: Timestamp.now(), updatedBy: userData.id } as any, { merge: true });
      } else {
        await setDoc(
          teacherDocRef,
          { [kind]: meta, updatedAt: Timestamp.now(), updatedBy: userData.id } as any,
          { merge: true }
        );
      }

      await loadCompliance();
    } catch (e: any) {
      console.error('Failed to upload compliance file', e);
      setComplianceError(e?.message ? String(e.message) : 'Upload failed. Please try again.');
    } finally {
      setUploadingComplianceKey(null);
    }
  }, [userData?.id, compliance?.signedContracts, loadCompliance]);

  const uploadSchoolClearance = useCallback(async (file: File) => {
    if (!userData?.id) return;
    if (!file) return;

    const partnerId = String(selectedClearanceSchoolId || '').trim();
    if (!partnerId) {
      setSchoolClearancesError('Please choose a partner.');
      return;
    }

    const type = selectedClearanceType;
    const otherLabel = String(selectedClearanceOtherLabel || '').trim();
    if (type === 'other' && !otherLabel) {
      setSchoolClearancesError('Please enter a label for “Other”.');
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setSchoolClearancesError('Please upload a PDF or an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSchoolClearancesError('File too large. Maximum size is 10MB.');
      return;
    }

    setSchoolClearancesError(null);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const rand = Math.random().toString(16).slice(2, 8);
    const ext = (file.name.split('.').pop() || (isPdf ? 'pdf' : 'png')).toLowerCase().replace(/[^a-z0-9]/g, '');
    const storagePath = `partners/${partnerId}/teacherClearances/${userData.id}/${type}/${stamp}-${rand}.${ext || (isPdf ? 'pdf' : 'png')}`;

    const key = `schoolClearance:${storagePath}`;
    setUploadingComplianceKey(key);
    try {
      const r = ref(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const url = await getDownloadURL(r);

      const fileMeta: UploadedFileMeta = {
        storagePath,
        url,
        filename: file.name || undefined,
        contentType: file.type || undefined,
        sizeBytes: file.size || undefined,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData.id,
      };
      const evidenceId = `${stamp}-${rand}`;
      const clearanceRef = doc(db, 'partners', partnerId, 'teacherClearances', userData.id);
      const prevSnap = await getDoc(clearanceRef);
      const prev: any = prevSnap.exists() ? prevSnap.data() : {};
      const requirementId = type === 'other'
        ? `other_${otherLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`
        : type;
      await setDoc(doc(collection(clearanceRef, 'evidence'), evidenceId), {
        id: evidenceId,
        partnerId,
        teacherId: userData.id,
        requirementId,
        file: fileMeta,
        submittedAt: Timestamp.now(),
        submittedBy: userData.id,
      });
      await setDoc(
        clearanceRef,
        {
          partnerId,
          teacherId: userData.id,
          cleared: false,
          missingRequirementIds: Array.isArray(prev?.missingRequirementIds) ? prev.missingRequirementIds : [],
          expiringRequirementIds: Array.isArray(prev?.expiringRequirementIds) ? prev.expiringRequirementIds : [],
          requirementStatuses: {
            ...(prev?.requirementStatuses || {}),
            [requirementId]: {
              requirementId,
              requirementVersion: 1,
              status: 'submitted',
              latestEvidenceId: evidenceId,
              completedAt: Timestamp.now(),
            },
          },
          updatedAt: Timestamp.now(),
          updatedBy: userData.id,
        } as any,
        { merge: true }
      );

      await loadSchoolClearances();
    } catch (e: any) {
      console.error('Failed to upload school clearance', e);
      setSchoolClearancesError(e?.message ? String(e.message) : 'Upload failed. Please try again.');
    } finally {
      setUploadingComplianceKey(null);
    }
  }, [
    userData?.id,
    selectedClearanceSchoolId,
    selectedClearanceType,
    selectedClearanceOtherLabel,
    loadSchoolClearances,
  ]);

  const MAX_HEADSHOT_SIZE_BYTES = 500 * 1024 * 1024;
  const MAX_HEADSHOT_SIZE_LABEL = '500MB';

  const headshotUploadErrorMessage = (error: any): string => {
    const code = String(error?.code || '').toLowerCase();
    if (code.includes('unauthorized')) {
      return 'Upload permission was denied. Please sign out, sign back in, and try again. If it continues, contact Overture support.';
    }
    if (code.includes('canceled')) return 'The upload was canceled.';
    if (code.includes('quota-exceeded')) return 'Storage is temporarily unavailable because its quota was exceeded.';
    return error?.message || 'Failed to upload. Please try again.';
  };

  const setProfilePhotoFromUrl = async (url: string, preferredExt?: string) => {
    if (!userData?.id) {
      setHeadshotError('Not authenticated');
      return;
    }
    setHeadshotError(null);
    setHeadshotSuccess(false);
    setUploadingHeadshot(true);

    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('Failed to fetch uploaded photo');
      const blob = await resp.blob();
      if (blob.size >= MAX_HEADSHOT_SIZE_BYTES) {
        throw new Error(`This image is too large. Headshots must be smaller than ${MAX_HEADSHOT_SIZE_LABEL}.`);
      }

      const extFromType =
        blob.type === 'image/png' ? 'png'
          : blob.type === 'image/gif' ? 'gif'
            : blob.type === 'image/webp' ? 'webp'
              : blob.type === 'image/jpeg' ? 'jpg'
                : null;
      const ext = (preferredExt || extFromType || 'jpg').replace(/[^a-z0-9]/gi, '').toLowerCase();

      const headshotPath = `teachers/${userData.id}/headshot.${ext || 'jpg'}`;
      const headshotRef = ref(storage, headshotPath);
      await uploadBytes(headshotRef, blob, blob.type ? { contentType: blob.type } : undefined);

      setLocalHeadshotPreviewUrl(url);
      setHeadshotSuccess(true);
      window.setTimeout(() => setHeadshotSuccess(false), 3000);
    } catch (e: any) {
      console.error('Failed to set profile photo', e);
      setHeadshotError(headshotUploadErrorMessage(e));
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const uploadHeadshot = async (file: File) => {
    if (!userData?.id) {
      setHeadshotError('Not authenticated');
      return;
    }

    // Validate file
    if (!file.type.startsWith('image/')) {
      setHeadshotError('File must be an image');
      return;
    }

    if (file.size >= MAX_HEADSHOT_SIZE_BYTES) {
      setHeadshotError(`This image is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Headshots must be smaller than ${MAX_HEADSHOT_SIZE_LABEL}.`);
      return;
    }

    setHeadshotError(null);
    setHeadshotSuccess(false);
    setUploadingHeadshot(true);

    try {
      // Immediate local preview (even before Cloud Function updates headshotUrl)
      try {
        setLocalHeadshotPreviewUrl(URL.createObjectURL(file));
      } catch {}

      // Determine extension
      const ext = (file.name.split('.').pop()?.toLowerCase() || 'jpg').replace(/[^a-z0-9]/gi, '');
      
      // Save to uploads (so teachers can select from history)
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const rand = Math.random().toString(16).slice(2, 8);
      const uploadPath = `teachers/${userData.id}/uploads/${stamp}-${rand}.${ext || 'jpg'}`;

      // Upload to private teacher folder (selected headshot)
      const headshotPath = `teachers/${userData.id}/headshot.${ext || 'jpg'}`;

      await Promise.all([
        uploadBytes(ref(storage, headshotPath), file),
        uploadBytes(ref(storage, uploadPath), file),
      ]);
      
      setHeadshotSuccess(true);
      window.setTimeout(() => setHeadshotSuccess(false), 3000);
      
      console.log('Headshot uploaded! Cloud Function will copy to public folder.');
      loadUploadedPhotos();
    } catch (e: any) {
      console.error('Failed to upload headshot', e);
      setHeadshotError(headshotUploadErrorMessage(e));
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadHeadshot(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadHeadshot(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const save = async () => {
    if (!userData?.id) return;
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const phoneDigits = digitsOnly(form.phoneNumber);
      const emergencyPhoneDigits = digitsOnly(form.emergencyPhone);
      const displayName = `${String(form.firstName || '').trim()} ${String(form.lastName || '').trim()}`.trim();
      const emergencyName = `${String(form.emergencyFirstName || '').trim()} ${String(form.emergencyLastName || '').trim()}`.trim();

      const hasCarPatch = (form as any).hasCar === null ? deleteField() : !!(form as any).hasCar;
      const hasLicensePatch = (form as any).hasDriversLicense === null ? deleteField() : !!(form as any).hasDriversLicense;
      const musicPrivateLessonsPatch =
        (form as any).musicPrivateLessons === null ? deleteField() : !!(form as any).musicPrivateLessons;

      const bioClamped = clampToMaxWords(String((form as any).bio || ''), BIO_MAX_WORDS);
      const musicTopics =
        Array.isArray((form as any).musicTopics)
          ? (form as any).musicTopics.map((s: any) => String(s).trim()).filter(Boolean)
          : [];

      const shippingLine1 = String((form as any).shippingLine1 || '').trim();
      const shippingLine2 = String((form as any).shippingLine2 || '').trim();
      const shippingCity = String((form as any).shippingCity || '').trim();
      const shippingState = String((form as any).shippingState || '').trim();
      const shippingZip = String((form as any).shippingZip || '').trim();
      const shippingCountry = String((form as any).shippingCountry || '').trim();
      const shippingPlaceId = String((form as any).shippingPlaceId || '').trim();
      const shippingFormattedAddress = String((form as any).shippingFormattedAddress || '').trim();

      const neighborhoodDerived =
        shippingCity && shippingState ? `${shippingCity}, ${shippingState}` : (shippingCity || shippingState || null);

      const ref = doc(db, 'users', userData.id);
      await updateDoc(ref, {
        displayName: displayName || null,
        bio: bioClamped || null,
        slackEmail: String((form as any).slackEmail || '').trim() || null,
        linkedinUrl: String((form as any).linkedinUrl || '').trim() || null,
        phoneNumber: phoneDigits || null,
        classReminderPreferences: {
          email: (form as any).classReminderEmail !== false,
          sms: (form as any).classReminderSms === true,
          smsConsentedAt: (form as any).classReminderSms === true
            ? ((userData as any)?.classReminderPreferences?.smsConsentedAt || Timestamp.now())
            : null,
          smsConsentVersion: (form as any).classReminderSms === true ? '2026-09-01' : null,
          smsOptedOutAt: (form as any).classReminderSms === true ? null : Timestamp.now(),
        },
        neighborhood: neighborhoodDerived,
        shippingAddress: {
          line1: shippingLine1 || null,
          line2: shippingLine2 || null,
          city: shippingCity || null,
          state: shippingState || null,
          zip: shippingZip || null,
          country: shippingCountry || null,
          placeId: shippingPlaceId || null,
          formattedAddress: shippingFormattedAddress || null,
        },
        hasCar: hasCarPatch,
        hasDriversLicense: hasLicensePatch,
        timeZone: String(form.timeZone || '').trim() || null,
        subjects: Array.isArray(form.subjects) ? form.subjects.filter(Boolean) : [],
        musicPrivateLessons: musicPrivateLessonsPatch,
        musicTopics,
        emergencyContact: {
          name: emergencyName,
          phone: emergencyPhoneDigits || '',
          relationship: form.emergencyRelationship.trim(),
        },
        ...(isAdmin ? { adminNotificationPreferences: (form as any).adminNotificationPreferences } : {}),
      });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (e: any) {
      console.error('Failed to save teacher profile', e);
      setError(e?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teacher-profile-page">
      <div className={`teacher-profile-card-page${profileView === 'notifications' ? ' is-notifications-view' : ''}`}>
        <div className="teacher-profile-header">
          <div>
            <div className="teacher-profile-title-page">{profileView === 'notifications' ? 'Notifications' : 'My Profile'}</div>
            <div className="teacher-profile-subtitle">
              {profileView === 'notifications' ? 'Decide what reaches you, where it arrives, and when.' : 'Update your contact + transportation info for scheduling.'}
            </div>
          </div>
          <button
            type="button"
            className="teacher-profile-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {isAdmin && <nav className="teacher-profile-tabs" aria-label="Profile settings">
          <button type="button" className={profileView === 'profile' ? 'is-active' : ''} onClick={() => setProfileView('profile')}>Profile</button>
          <button type="button" className={profileView === 'notifications' ? 'is-active' : ''} onClick={() => setProfileView('notifications')}>Notifications</button>
        </nav>}

        {error && <div className="teacher-profile-error">{error}</div>}
        {saved && <div className="teacher-profile-saved">Saved</div>}

        {isAdmin && profileView === 'notifications' && <AdminNotificationSettings
          preferences={(form as any).adminNotificationPreferences}
          onChange={(preferences) => setForm(value => ({ ...(value as any), adminNotificationPreferences: preferences }))}
          email={String(userData?.email || '')}
          phone={formatUsPhoneDisplay(String(userData?.phoneNumber || ''))}
          slackEmail={String((userData as any)?.slackEmail || '')}
          saving={saving}
          onSave={save}
        />}

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Profile</div>
          <div className="teacher-profile-grid">
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>First name</span>
                {missing.firstName && (
                  <span className="teacher-profile-inline-badge" aria-label="First name required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String(form.firstName || '')}
                onChange={(e) => setForm(v => ({ ...v, firstName: e.target.value }))}
                placeholder="First name"
              />
            </label>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Last name</span>
                {missing.lastName && (
                  <span className="teacher-profile-inline-badge" aria-label="Last name required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String(form.lastName || '')}
                onChange={(e) => setForm(v => ({ ...v, lastName: e.target.value }))}
                placeholder="Last name"
              />
            </label>
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Contact</div>
          <div className="teacher-profile-grid">
            <div className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Email used for slack account</span>
                {missing.slackEmail && (
                  <span className="teacher-profile-inline-badge" aria-label="Slack email required" title="Required for Slack notifications">
                    !
                  </span>
                )}
              </div>
              <input
                value={String((form as any).slackEmail || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), slackEmail: e.target.value }))}
                placeholder="name@school.org"
                type="email"
                inputMode="email"
                aria-label="Email used for Slack account (for Slack notifications)"
              />
              <div className="teacher-profile-subtext">
                used for notifications about subbing + more
              </div>
            </div>
            <div className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>LinkedIn profile URL</span>
              </div>
              <input
                value={String((form as any).linkedinUrl || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), linkedinUrl: e.target.value }))}
                placeholder="https://linkedin.com/in/yourprofile"
                type="url"
                aria-label="LinkedIn profile URL"
              />
              <div className="teacher-profile-subtext">
                shown on your public profile on our website — <strong>Add Overture Teacher to your experience in LinkedIn!</strong>
              </div>
            </div>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Phone number</span>
                {missing.phoneNumber && (
                  <span className="teacher-profile-inline-badge" aria-label="Phone required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                inputMode="tel"
                value={formatUsPhoneDisplay(form.phoneNumber)}
                onChange={(e) => setForm(v => ({ ...v, phoneNumber: digitsOnly(e.target.value) }))}
                placeholder="(555) 555-5555"
              />
            </label>
            <div
              ref={shippingAddressFieldRef}
              className="teacher-profile-field"
              style={{ position: 'relative', gridColumn: '1 / -1' }}
            >
              <div className="teacher-profile-inline-title">
                <span>Street address line 1</span>
                {missing.shippingLine1 && (
                  <span className="teacher-profile-inline-badge" aria-label="Street address required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String((form as any).shippingLine1 || '')}
                onChange={(e) => setForm(v => ({
                  ...(v as any),
                  shippingLine1: e.target.value,
                  shippingPlaceId: '',
                  shippingFormattedAddress: '',
                }))}
                onFocus={() => setIsShippingAddressInputFocused(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    closeShippingAddressSuggestions();
                  }
                }}
                placeholder="123 Main St"
                onBlur={() => {
                  // give suggestion click a chance to run (mousedown)
                  window.setTimeout(() => {
                    closeShippingAddressSuggestions();
                  }, 120);
                }}
              />
              <div className="teacher-profile-subtext">
                We use this address to ship you Overture merch, devices, and more.
              </div>

              {isShippingAddressInputFocused && (shippingAddressGoogleLoading || shippingAddressGoogleError || shippingAddressGooglePredictions.length > 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 54px)', // below input + subtext
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    background: '#fff',
                    border: '1px solid rgba(15, 23, 42, 0.18)',
                    borderRadius: 12,
                    boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                  }}
                >
                  {shippingAddressGoogleLoading && (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b', fontWeight: 700 }}>
                      Searching…
                    </div>
                  )}
                  {shippingAddressGoogleError && (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: '#b45309', borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
                      {shippingAddressGoogleError} (you can still type anything)
                    </div>
                  )}

                  {(() => {
                    const query = String((form as any).shippingLine1 || '').trim();
                    const googleSuggestions = shippingAddressGooglePredictions
                      .map(p => ({
                        key: `g:${p.placeId}`,
                        label: normalizeGoogleAddressLabel(p.description),
                        source: 'google' as const,
                        placeId: p.placeId,
                        rawDescription: p.description,
                      }))
                      .filter(s => s.label.length > 0);

                    const typedSuggestion = query.length > 0
                      ? [{ key: `t:${query}`, label: `Use “${query}”`, source: 'typed' as const }]
                      : [];

                    const seen = new Set<string>();
                    const suggestions = [...typedSuggestion, ...googleSuggestions].filter((s) => {
                      const k = s.label.toLowerCase();
                      if (seen.has(k)) return false;
                      seen.add(k);
                      return true;
                    });

                    if (suggestions.length === 0) {
                      return (
                        <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b', fontWeight: 700 }}>
                          No matches
                        </div>
                      );
                    }

                    return suggestions.map((sug: any) => (
                      <button
                        key={sug.key}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (sug.source === 'typed') {
                            setForm(v => ({ ...(v as any), shippingLine1: query }));
                          } else if (sug.source === 'google' && sug.placeId) {
                            void applyShippingAddressFromPlaceId(String(sug.placeId));
                          }
                          closeShippingAddressSuggestions();
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 12px',
                          fontSize: '13px',
                          fontWeight: 800,
                          border: 'none',
                          background: '#fff',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {sug.label}
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>

            <label className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>Street address line 2 (Apt / Unit)</span>
              </div>
              <input
                value={String((form as any).shippingLine2 || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), shippingLine2: e.target.value }))}
                placeholder="Apt 4B"
              />
            </label>

            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>City</span>
                {missing.shippingCity && (
                  <span className="teacher-profile-inline-badge" aria-label="City required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String((form as any).shippingCity || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), shippingCity: e.target.value }))}
                placeholder="City"
              />
            </label>

            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>State</span>
                {missing.shippingState && (
                  <span className="teacher-profile-inline-badge" aria-label="State required" title="Required">
                    !
                  </span>
                )}
              </div>
              <select
                value={String((form as any).shippingState || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), shippingState: e.target.value }))}
              >
                <option value="">Select…</option>
                {US_STATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>ZIP</span>
                {missing.shippingZip && (
                  <span className="teacher-profile-inline-badge" aria-label="ZIP required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                inputMode="numeric"
                value={String((form as any).shippingZip || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), shippingZip: e.target.value }))}
                placeholder="ZIP"
              />
            </label>

            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Country</span>
                {missing.shippingCountry && (
                  <span className="teacher-profile-inline-badge" aria-label="Country required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String((form as any).shippingCountry || '')}
                onChange={(e) => setForm(v => ({ ...(v as any), shippingCountry: e.target.value }))}
                placeholder="United States"
              />
            </label>
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Scheduling</div>
          <div className="teacher-profile-availability-callout">
            <div>
              <strong>Teaching availability</strong>
              <span>Update your weekly schedule and add exceptions for specific dates.</span>
            </div>
            <Link className="teacher-profile-availability-link" to="/app/availability">
              Update availability
            </Link>
          </div>
          <div className="teacher-profile-grid">
            <div className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>24-hour class reminders</span>
              </div>
              <div className="teacher-profile-subtext" style={{ marginBottom: 10 }}>
                We’ll send the details about 24 hours before each class you’re teaching. You can turn off either channel.
              </div>
              <div className="teacher-profile-reminder-options">
                <label className="teacher-profile-toggle">
                  <input
                    type="checkbox"
                    checked={(form as any).classReminderEmail !== false}
                    onChange={(e) => setForm(v => ({ ...(v as any), classReminderEmail: e.target.checked }))}
                  />
                  Email reminders
                </label>
                <label className="teacher-profile-toggle">
                  <input
                    type="checkbox"
                    checked={(form as any).classReminderSms === true}
                    onChange={(e) => setForm(v => ({ ...(v as any), classReminderSms: e.target.checked }))}
                  />
                  Text reminders (optional)
                </label>
              </div>
              <div className="teacher-profile-subtext" style={{ marginTop: 10 }}>
                By selecting Text reminders and saving, you agree to receive recurring automated class,
                schedule, and assignment texts from Overture at the mobile number in your profile. Message
                frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.
                Consent is not a condition of purchase. Your mobile information will not be sold or
                shared with third parties for promotional or marketing purposes. See our{' '}
                <a href="https://www.overtureai.org/legal/sms-terms" target="_blank" rel="noreferrer">SMS Terms</a>
                {' '}and{' '}
                <a href="https://www.overtureai.org/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
              </div>
              {(form as any).classReminderSms === true && !String(form.phoneNumber || '').trim() && (
                <div className="teacher-profile-subtext teacher-profile-reminder-warning">
                  Add a mobile number under Contact to receive texts.
                </div>
              )}
            </div>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Time zone</span>
                {missing.timeZone && (
                  <span className="teacher-profile-inline-badge" aria-label="Time zone required" title="Required">
                    !
                  </span>
                )}
              </div>
              <select
                value={form.timeZone}
                onChange={(e) => setForm(v => ({ ...v, timeZone: e.target.value }))}
              >
                {TIMEZONE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Subjects</span>
                {missing.subjects && (
                  <span className="teacher-profile-inline-badge" aria-label="Subjects required" title="Select at least one subject">
                    !
                  </span>
                )}
              </div>
              <div className="teacher-profile-chips">
                {SUBJECT_OPTIONS.map(opt => {
                  const checked = Array.isArray(form.subjects) && form.subjects.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`teacher-profile-chip ${checked ? 'is-selected' : ''}`}
                      onClick={() => {
                        setForm(v => {
                          const current = Array.isArray(v.subjects) ? v.subjects : [];
                          const next = current.includes(opt.value)
                            ? current.filter(x => x !== opt.value)
                            : [...current, opt.value];
                          return { ...v, subjects: next };
                        });
                      }}
                    >
                      <span className="teacher-profile-chip-check" aria-hidden>
                        {checked ? '✓' : ''}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {Array.isArray(form.subjects) && form.subjects.includes('music') && (
              <div className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
                <div className="teacher-profile-inline-title">
                  <span>Teaches private lessons</span>
                  {missing.musicPrivateLessons && (
                    <span
                      className="teacher-profile-inline-badge"
                      aria-label="Private lessons selection required"
                      title="Required when Music is selected"
                    >
                      !
                    </span>
                  )}
                </div>
                <div className="teacher-profile-yesno" role="radiogroup" aria-label="Teaches private lessons">
                  <button
                    type="button"
                    className={`teacher-profile-yesno-btn ${(form as any).musicPrivateLessons === true ? 'is-selected' : ''}`}
                    onClick={() => setForm(v => ({ ...(v as any), musicPrivateLessons: true }))}
                    aria-checked={(form as any).musicPrivateLessons === true}
                    role="radio"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`teacher-profile-yesno-btn ${(form as any).musicPrivateLessons === false ? 'is-selected' : ''}`}
                    onClick={() => setForm(v => ({ ...(v as any), musicPrivateLessons: false }))}
                    aria-checked={(form as any).musicPrivateLessons === false}
                    role="radio"
                  >
                    No
                  </button>
                </div>

                {(form as any).musicPrivateLessons === true && (
                  <div className="teacher-profile-field" style={{ marginTop: 10 }}>
                    <div className="teacher-profile-inline-title">
                      <span>Instruments / concepts taught (select all that apply)</span>
                      {missing.musicTopics && (
                        <span
                          className="teacher-profile-inline-badge"
                          aria-label="Music topics required"
                          title="Required when teaching private lessons"
                        >
                          !
                        </span>
                      )}
                    </div>
                    <select
                      multiple
                      value={Array.isArray((form as any).musicTopics) ? (form as any).musicTopics : []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                        setForm(v => ({ ...(v as any), musicTopics: selected }));
                      }}
                      size={10}
                      aria-label="Instruments and concepts taught"
                    >
                      {MUSIC_TOPIC_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="teacher-profile-subtext">
                      Hold Command (Mac) / Ctrl (Windows) to select multiple
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Transportation</div>
          <div className="teacher-profile-grid">
            <div className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Has car</span>
                {missing.hasCar && (
                  <span className="teacher-profile-inline-badge" aria-label="Car selection required" title="Select Yes or No">
                    !
                  </span>
                )}
              </div>
              <div className="teacher-profile-yesno" role="radiogroup" aria-label="Has car">
                <button
                  type="button"
                  className={`teacher-profile-yesno-btn ${form.hasCar === true ? 'is-selected' : ''}`}
                  onClick={() => setForm(v => ({ ...v, hasCar: true }))}
                  aria-checked={form.hasCar === true}
                  role="radio"
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`teacher-profile-yesno-btn ${form.hasCar === false ? 'is-selected' : ''}`}
                  onClick={() => setForm(v => ({ ...v, hasCar: false }))}
                  aria-checked={form.hasCar === false}
                  role="radio"
                >
                  No
                </button>
              </div>
            </div>

            <div className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Has driver’s license</span>
                {missing.hasDriversLicense && (
                  <span className="teacher-profile-inline-badge" aria-label="License selection required" title="Select Yes or No">
                    !
                  </span>
                )}
              </div>
              <div className="teacher-profile-yesno" role="radiogroup" aria-label="Has driver's license">
                <button
                  type="button"
                  className={`teacher-profile-yesno-btn ${form.hasDriversLicense === true ? 'is-selected' : ''}`}
                  onClick={() => setForm(v => ({ ...v, hasDriversLicense: true }))}
                  aria-checked={form.hasDriversLicense === true}
                  role="radio"
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`teacher-profile-yesno-btn ${form.hasDriversLicense === false ? 'is-selected' : ''}`}
                  onClick={() => setForm(v => ({ ...v, hasDriversLicense: false }))}
                  aria-checked={form.hasDriversLicense === false}
                  role="radio"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Learning and certifications</div>
          <div className="teacher-profile-subtext" style={{ marginBottom: 10 }}>
            Upload PDFs or images for your required learning and certifications.
          </div>

          {complianceError && <div className="teacher-profile-error">{complianceError}</div>}

          <div className="teacher-profile-grid">
            <div className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>Mandatory reporting certification</span>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <label className="teacher-profile-save" style={{ cursor: 'pointer' }}>
                  {uploadingComplianceKey?.startsWith('mandatoryReportingCertificationFile:') ? 'Uploading…' : 'Upload'}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadTeacherComplianceFile('mandatoryReportingCertificationFile', f);
                      e.target.value = '';
                    }}
                    disabled={!!uploadingComplianceKey}
                  />
                </label>
                {complianceLoading ? (
                  <span className="teacher-profile-subtext">Loading…</span>
                ) : compliance?.mandatoryReportingCertificationFile?.url ? (
                  <a
                    href={compliance.mandatoryReportingCertificationFile.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13, fontWeight: 800 }}
                  >
                    View current
                  </a>
                ) : (
                  <span className="teacher-profile-subtext">No file uploaded yet.</span>
                )}
              </div>
            </div>

            <div className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>Signed contracts</span>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <label className="teacher-profile-save" style={{ cursor: 'pointer' }}>
                  {uploadingComplianceKey?.startsWith('signedContract:') ? 'Uploading…' : 'Upload'}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadTeacherComplianceFile('signedContract', f);
                      e.target.value = '';
                    }}
                    disabled={!!uploadingComplianceKey}
                  />
                </label>
                <span className="teacher-profile-subtext">
                  {Array.isArray(compliance?.signedContracts) ? `${compliance?.signedContracts.length} uploaded` : '0 uploaded'}
                </span>
              </div>
              {Array.isArray(compliance?.signedContracts) && compliance!.signedContracts!.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {compliance!.signedContracts!.slice(0, 6).map((m, idx) => (
                    <a key={`${m.storagePath || idx}`} href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 800 }}>
                      {m.filename || `Contract ${idx + 1}`}
                    </a>
                  ))}
                  {compliance!.signedContracts!.length > 6 && (
                    <div className="teacher-profile-subtext">
                      Showing 6 of {compliance!.signedContracts!.length}.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>Partner-specific requirements</span>
              </div>
              <div className="teacher-profile-subtext" style={{ marginBottom: 8 }}>
                Choose the partner and requirement type, then upload the confirmation file. Only upload a TB test when that partner requires it.
              </div>

              {schoolClearancesError && <div className="teacher-profile-error" style={{ marginBottom: 8 }}>{schoolClearancesError}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 1fr', gap: 10, alignItems: 'end' }}>
                <label className="teacher-profile-field" style={{ margin: 0 }}>
                  <div className="teacher-profile-inline-title"><span>Partner</span></div>
                  <select value={selectedClearanceSchoolId} onChange={(e) => setSelectedClearanceSchoolId(e.target.value)}>
                    <option value="">Select partner…</option>
                    {schoolsForClearances.map((s) => (
                      <option key={s.id} value={s.id}>{s.name || s.id}</option>
                    ))}
                  </select>
                </label>
                <label className="teacher-profile-field" style={{ margin: 0 }}>
                  <div className="teacher-profile-inline-title"><span>Type</span></div>
                  <select value={selectedClearanceType} onChange={(e) => setSelectedClearanceType(e.target.value as TeacherBgCheckType)}>
                    <option value="cori">CORI</option>
                    <option value="fingerprint">Fingerprint</option>
                    <option value="tb_test">TB test</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="teacher-profile-field" style={{ margin: 0 }}>
                  <div className="teacher-profile-inline-title"><span>Upload</span></div>
                  <label className="teacher-profile-save" style={{ cursor: 'pointer', display: 'inline-block', width: 'fit-content' }}>
                    {uploadingComplianceKey?.startsWith('schoolClearance:') ? 'Uploading…' : 'Upload file'}
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadSchoolClearance(f);
                        e.target.value = '';
                      }}
                      disabled={!!uploadingComplianceKey}
                    />
                  </label>
                </label>
              </div>

              {selectedClearanceType === 'other' && (
                <div style={{ marginTop: 10 }}>
                  <label className="teacher-profile-field" style={{ margin: 0 }}>
                    <div className="teacher-profile-inline-title"><span>Other label</span></div>
                    <input
                      value={selectedClearanceOtherLabel}
                      onChange={(e) => setSelectedClearanceOtherLabel(e.target.value)}
                      placeholder="e.g., District portal clearance"
                    />
                  </label>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                {schoolClearancesLoading ? (
                  <div className="teacher-profile-subtext">Loading…</div>
                ) : schoolClearances.length === 0 ? (
                  <div className="teacher-profile-subtext">No partner clearances or historical school records uploaded yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {schoolClearances.map((sc) => (
                      <div key={sc.schoolId} style={{ padding: 10, border: '1px solid rgba(15, 23, 42, 0.12)', borderRadius: 12 }}>
                        <div style={{ fontWeight: 900, fontSize: 13 }}>
                          {sc.schoolName || schoolsForClearances.find(s => s.id === sc.schoolId)?.name || sc.schoolId}
                        </div>
                        {Array.isArray(sc.items) && sc.items.length > 0 ? (
                          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {sc.items.slice(0, 6).map((it) => {
                              const label =
                                it.type === 'cori' ? 'CORI'
                                  : it.type === 'fingerprint' ? 'Fingerprint'
                                    : it.type === 'tb_test' ? 'TB test'
                                      : it.type === 'other' ? (it.typeLabel || 'Other')
                                        : it.type;
                              return (
                                <a
                                  key={it.id}
                                  href={it.file?.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ fontSize: 13, fontWeight: 800 }}
                                >
                                  {label}{it.file?.filename ? ` — ${it.file.filename}` : ''}
                                </a>
                              );
                            })}
                            {sc.items.length > 6 && (
                              <div className="teacher-profile-subtext">Showing 6 of {sc.items.length}.</div>
                            )}
                          </div>
                        ) : (
                          <div className="teacher-profile-subtext" style={{ marginTop: 6 }}>
                            No uploads yet for this school.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Emergency contact</div>
          <div className="teacher-profile-grid">
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>First name</span>
                {missing.emergencyFirstName && (
                  <span className="teacher-profile-inline-badge" aria-label="Emergency first name required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String(form.emergencyFirstName || '')}
                onChange={(e) => setForm(v => ({ ...v, emergencyFirstName: e.target.value }))}
                placeholder="First name"
              />
            </label>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Last name</span>
                {missing.emergencyLastName && (
                  <span className="teacher-profile-inline-badge" aria-label="Emergency last name required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={String(form.emergencyLastName || '')}
                onChange={(e) => setForm(v => ({ ...v, emergencyLastName: e.target.value }))}
                placeholder="Last name"
              />
            </label>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Phone</span>
                {missing.emergencyPhone && (
                  <span className="teacher-profile-inline-badge" aria-label="Emergency phone required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                inputMode="tel"
                value={formatUsPhoneDisplay(form.emergencyPhone)}
                onChange={(e) => setForm(v => ({ ...v, emergencyPhone: digitsOnly(e.target.value) }))}
                placeholder="(555) 555-5555"
              />
            </label>
            <label className="teacher-profile-field">
              <div className="teacher-profile-inline-title">
                <span>Relationship</span>
                {missing.emergencyRelationship && (
                  <span className="teacher-profile-inline-badge" aria-label="Emergency relationship required" title="Required">
                    !
                  </span>
                )}
              </div>
              <input
                value={form.emergencyRelationship}
                onChange={(e) => setForm(v => ({ ...v, emergencyRelationship: e.target.value }))}
                placeholder="Partner / Parent / Friend"
              />
            </label>
          </div>
        </div>

        <div className="teacher-profile-section">
          <div className="teacher-profile-section-title">Public profile</div>
          <div className="teacher-profile-subtext" style={{ marginBottom: 10 }}>
            These will be published on our website.
          </div>

          <div className="teacher-profile-headshot-container" style={{ marginBottom: 14 }}>
            {(localHeadshotPreviewUrl || userData?.headshotUrl) && (
              <div className="teacher-profile-headshot-preview">
                <img src={localHeadshotPreviewUrl || (userData as any)?.headshotUrl} alt="Your profile photo" />
              </div>
            )}
            <div
              className={`teacher-profile-headshot-upload ${isDragging ? 'is-dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {uploadingHeadshot ? (
                <div className="teacher-profile-headshot-uploading">
                  <div className="teacher-profile-headshot-spinner"></div>
                  <div>Uploading...</div>
                </div>
              ) : (
                <>
                  <div className="teacher-profile-headshot-icon">📸</div>
                  <div className="teacher-profile-headshot-text">
                    <strong>Drag & drop</strong> your photo here or <strong>click to upload</strong>
                  </div>
                  <div className="teacher-profile-headshot-subtext">
                    JPG, PNG, GIF, or WebP • Max 500MB
                  </div>
                </>
              )}
            </div>
            {headshotError && <div className="teacher-profile-error" style={{ marginTop: 10 }}>{headshotError}</div>}
            {headshotSuccess && <div className="teacher-profile-saved" style={{ marginTop: 10 }}>Photo uploaded! It will appear on the website shortly.</div>}

            <div style={{ marginTop: 10 }}>
              <div className="teacher-profile-subtext" style={{ marginBottom: 6 }}>
                Choose from your uploads:
              </div>
              {uploadsLoading ? (
                <div className="teacher-profile-subtext">Loading uploads…</div>
              ) : uploadsError ? (
                <div className="teacher-profile-subtext" style={{ color: '#b45309' }}>
                  {uploadsError}
                </div>
              ) : uploadedPhotos.length === 0 ? (
                <div className="teacher-profile-subtext">No previous uploads yet.</div>
              ) : (
                <div className="teacher-profile-headshot-gallery" role="list" aria-label="Uploaded photos">
                  {uploadedPhotos.slice(0, 12).map((p) => {
                    const currentUrl = localHeadshotPreviewUrl || (userData as any)?.headshotUrl || '';
                    const isSelected = !!currentUrl && currentUrl === p.url;
                    const ext = p.name.split('.').pop()?.toLowerCase();
                    return (
                      <button
                        key={p.path}
                        type="button"
                        className={`teacher-profile-headshot-thumb ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => setProfilePhotoFromUrl(p.url, ext)}
                        title="Set as profile photo"
                        aria-label="Set as profile photo"
                        disabled={uploadingHeadshot}
                      >
                        <img src={p.url} alt="" />
                      </button>
                    );
                  })}
                </div>
              )}
              {uploadedPhotos.length > 12 && (
                <button
                  type="button"
                  className="teacher-profile-save"
                  style={{ marginTop: 10 }}
                  onClick={loadUploadedPhotos}
                  disabled={uploadsLoading}
                >
                  Refresh uploads
                </button>
              )}
            </div>
          </div>

          <div className="teacher-profile-grid">
            <label className="teacher-profile-field" style={{ gridColumn: '1 / -1' }}>
              <div className="teacher-profile-inline-title">
                <span>Public bio (100 words or less)</span>
                {missing.bio && (
                  <span className="teacher-profile-inline-badge" aria-label="Public bio required" title="Required (100 words or less)">
                    !
                  </span>
                )}
              </div>
              <textarea
                value={String((form as any).bio || '')}
                onChange={(e) => {
                  const next = clampToMaxWords(e.target.value, BIO_MAX_WORDS);
                  setForm(v => ({ ...(v as any), bio: next }));
                }}
                placeholder="Tell us a little about yourself…"
                rows={4}
              />
              <div className="teacher-profile-subtext">
                {countWords(String((form as any).bio || ''))} / {BIO_MAX_WORDS} words
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfilePage;
